import React, { useState, useEffect } from "react";
import { useAdmin } from "../../context/AdminContext";
import { 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  AlertCircle, 
  CheckCircle,
  Eye,
  EyeOff,
  Key,
  User,
  Mail,
  Shield,
  ShieldOff
} from "lucide-react";

const UserManagement = () => {
  const { adminAPI, adminUser } = useAdmin();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  const [createData, setCreateData] = useState({
    username: "",
    password: "",
    full_name: "",
    email: ""
  });

  const [editData, setEditData] = useState({
    username: "",
    full_name: "",
    email: "",
    new_password: "",
    confirm_password: ""
  });

  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false,
    create: false,
    edit_new: false,
    edit_confirm: false
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await adminAPI.getAdminUsers();
      setUsers(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement des utilisateurs:", error);
      setMessage({ type: "error", text: "Erreur lors du chargement des utilisateurs" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      if (createData.password.length < 6) {
        setMessage({ type: "error", text: "Le mot de passe doit contenir au moins 6 caractères" });
        return;
      }

      await adminAPI.createAdminUser(createData);
      setMessage({ type: "success", text: "Utilisateur créé avec succès !" });
      loadUsers();
      setShowCreateForm(false);
      setCreateData({ username: "", password: "", full_name: "", email: "" });
    } catch (error) {
      setMessage({ type: "error", text: error.response?.data?.detail || "Erreur lors de la création" });
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditData({
      username: user.username,
      full_name: user.full_name || "",
      email: user.email || "",
      new_password: "",
      confirm_password: ""
    });
    setShowEditForm(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      // Check if passwords match if provided
      if (editData.new_password && editData.new_password !== editData.confirm_password) {
        setMessage({ type: "error", text: "Les mots de passe ne correspondent pas" });
        return;
      }

      // Update profile information
      const profileUpdate = {
        full_name: editData.full_name,
        email: editData.email
      };

      if (editingUser.username === adminUser) {
        // Update own profile
        await adminAPI.updateAdminProfile(profileUpdate);
      } else {
        // Update other user's profile via admin API
        await adminAPI.updateUserProfile(editingUser.id, profileUpdate);
      }

      // Update password if provided
      if (editData.new_password) {
        if (editingUser.username === adminUser) {
          // Need current password for own account
          setMessage({ type: "error", text: "Pour changer votre propre mot de passe, utilisez la fonction de changement de mot de passe sécurisée" });
          return;
        } else {
          // Admin can reset other user passwords
          await adminAPI.resetUserPassword(editingUser.id, editData.new_password);
        }
      }

      setMessage({ type: "success", text: "Utilisateur mis à jour avec succès !" });
      loadUsers();
      setShowEditForm(false);
      setEditingUser(null);
    } catch (error) {
      setMessage({ type: "error", text: error.response?.data?.detail || "Erreur lors de la mise à jour" });
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      await adminAPI.toggleUserStatus(userId);
      setMessage({ type: "success", text: "Statut utilisateur mis à jour !" });
      loadUsers();
    } catch (error) {
      setMessage({ type: "error", text: error.response?.data?.detail || "Erreur lors de la mise à jour" });
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur "${username}" ?`)) {
      try {
        await adminAPI.deleteAdminUser(userId);
        setMessage({ type: "success", text: "Utilisateur supprimé avec succès !" });
        loadUsers();
      } catch (error) {
        setMessage({ type: "error", text: error.response?.data?.detail || "Erreur lors de la suppression" });
      }
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestion des Utilisateurs</h2>
          <p className="text-gray-600 mt-1">{users.length} utilisateur(s) admin</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center space-x-2 bg-blue-900 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors duration-200"
          >
            <Plus className="w-4 h-4" />
            <span>Nouvel utilisateur</span>
          </button>
        </div>
      </div>

      {message && (
        <div className={`mb-6 flex items-center space-x-2 p-4 rounded-lg ${
          message.type === "success" 
            ? "bg-green-50 text-green-800 border border-green-200" 
            : "bg-red-50 text-red-800 border border-red-200"
        }`}>
          {message.type === "success" ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Users List */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Utilisateurs Administrateurs</h3>
          <div className="space-y-4">
            {users.map((user) => (
              <div key={user.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className={`p-2 rounded-full ${user.is_active ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                    {user.is_active ? <Shield className="w-5 h-5" /> : <ShieldOff className="w-5 h-5" />}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      {user.username}
                      {user.username === adminUser && (
                        <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                          Vous
                        </span>
                      )}
                    </h4>
                    <p className="text-sm text-gray-600">{user.full_name || "Nom non renseigné"}</p>
                    <p className="text-xs text-gray-500">{user.email || "Email non renseigné"}</p>
                    <p className="text-xs text-gray-400">
                      Créé le {new Date(user.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    user.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {user.is_active ? 'Actif' : 'Inactif'}
                  </span>
                  
                  <button
                    onClick={() => handleEditUser(user)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                    title="Modifier"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  
                  {user.username !== adminUser && (
                    <>
                      <button
                        onClick={() => handleToggleStatus(user.id)}
                        className={`p-2 rounded-lg transition-colors duration-200 ${
                          user.is_active 
                            ? 'text-red-600 hover:bg-red-50' 
                            : 'text-green-600 hover:bg-green-50'
                        }`}
                        title={user.is_active ? 'Désactiver' : 'Activer'}
                      >
                        {user.is_active ? <ShieldOff className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id, user.username)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Nouvel Utilisateur Admin</h3>
                <button onClick={() => setShowCreateForm(false)}>
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>

              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom d'utilisateur *
                  </label>
                  <input
                    type="text"
                    value={createData.username}
                    onChange={(e) => setCreateData(prev => ({...prev, username: e.target.value}))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mot de passe *
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.create ? "text" : "password"}
                      value={createData.password}
                      onChange={(e) => setCreateData(prev => ({...prev, password: e.target.value}))}
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('create')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    >
                      {showPasswords.create ? <EyeOff className="w-5 h-5 text-gray-400" /> : <Eye className="w-5 h-5 text-gray-400" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom complet
                  </label>
                  <input
                    type="text"
                    value={createData.full_name}
                    onChange={(e) => setCreateData(prev => ({...prev, full_name: e.target.value}))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={createData.email}
                    onChange={(e) => setCreateData(prev => ({...prev, email: e.target.value}))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                  />
                </div>

                <div className="flex justify-end space-x-4 pt-6">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="flex items-center space-x-2 bg-blue-900 text-white px-6 py-2 rounded-lg hover:bg-blue-800"
                  >
                    <Save className="w-4 h-4" />
                    <span>Créer</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditForm && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  Modifier - {editingUser.username}
                  {editingUser.username === adminUser && (
                    <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      Vous
                    </span>
                  )}
                </h3>
                <button onClick={() => setShowEditForm(false)}>
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>

              <form onSubmit={handleUpdateUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom d'utilisateur
                  </label>
                  <input
                    type="text"
                    value={editData.username}
                    disabled
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Le nom d'utilisateur ne peut pas être modifié</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom complet
                  </label>
                  <input
                    type="text"
                    value={editData.full_name}
                    onChange={(e) => setEditData(prev => ({...prev, full_name: e.target.value}))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={editData.email}
                    onChange={(e) => setEditData(prev => ({...prev, email: e.target.value}))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                  />
                </div>

                {editingUser.username !== adminUser && (
                  <>
                    <div className="pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Changer le mot de passe (optionnel)</h4>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nouveau mot de passe
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords.edit_new ? "text" : "password"}
                          value={editData.new_password}
                          onChange={(e) => setEditData(prev => ({...prev, new_password: e.target.value}))}
                          className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                          placeholder="Laisser vide pour ne pas changer"
                        />
                        <button
                          type="button"
                          onClick={() => togglePasswordVisibility('edit_new')}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2"
                        >
                          {showPasswords.edit_new ? <EyeOff className="w-5 h-5 text-gray-400" /> : <Eye className="w-5 h-5 text-gray-400" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Confirmer le nouveau mot de passe
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords.edit_confirm ? "text" : "password"}
                          value={editData.confirm_password}
                          onChange={(e) => setEditData(prev => ({...prev, confirm_password: e.target.value}))}
                          className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                          placeholder="Confirmer le nouveau mot de passe"
                        />
                        <button
                          type="button"
                          onClick={() => togglePasswordVisibility('edit_confirm')}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2"
                        >
                          {showPasswords.edit_confirm ? <EyeOff className="w-5 h-5 text-gray-400" /> : <Eye className="w-5 h-5 text-gray-400" />}
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {editingUser.username === adminUser && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> Pour changer votre propre mot de passe de façon sécurisée, 
                      utilisez une fonction dédiée qui demande votre mot de passe actuel.
                    </p>
                  </div>
                )}

                <div className="flex justify-end space-x-4 pt-6">
                  <button
                    type="button"
                    onClick={() => setShowEditForm(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="flex items-center space-x-2 bg-blue-900 text-white px-6 py-2 rounded-lg hover:bg-blue-800"
                  >
                    <Save className="w-4 h-4" />
                    <span>Sauvegarder</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;